<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';

  /**
   * The editor's footer (plan §5.12): «Sin cambios» / «1 cambio sin
   * guardar» / «✓ Guardado» at the left; «Cerrar» when clean, else
   * «Descartar» + «Guardar»; the create buttons; «Guardar lo que falta»
   * after a partial save. Buttons are disabled while saving (never
   * `loading`: the editor may close under them).
   */
  const props = withDefaults(
    defineProps<{
      changeCount: number;
      saving: boolean;
      savedFlash?: boolean;
      /** Nothing can be saved here (the role or a deleted product). */
      readOnly?: boolean;
      /** «Crear y agregar otro» + «Crear producto». */
      create?: boolean;
      /** Translations or stock failed: «Guardar lo que falta». */
      retry?: boolean;
      online: boolean;
      /** Disables «Guardar» (e.g. the product was deleted). */
      saveDisabled?: boolean;
      /** Label of the create button (a category has its own). */
      createText?: string;
      /** Shows «Crear y agregar otro». */
      createAnother?: boolean;
    }>(),
    {
      savedFlash: false,
      readOnly: false,
      create: false,
      retry: false,
      saveDisabled: false,
      createText: undefined,
      createAnother: true,
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'discard'): void;
    (e: 'save'): void;
    (e: 'save-another'): void;
    (e: 'retry'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;

  const status = computed(() => {
    if (props.readOnly) return '';
    // Also while a create waits for a photo: the disabled buttons need a why.
    if (props.saving) return String(proxy.$t('menuPage.editor.saving'));
    if (props.create) return '';
    if (props.changeCount)
      return String(
        proxy.$tc('menuPage.editor.changes', props.changeCount, {
          n: props.changeCount,
        })
      );
    if (props.savedFlash) return String(proxy.$t('menuPage.editor.saved'));
    return String(proxy.$t('menuPage.editor.noChanges'));
  });

  const offlineTitle = computed(() =>
    props.online ? undefined : String(proxy.$t('menuPage.errors.offline'))
  );
</script>

<template>
  <footer class="v2-catalog-editor-footer">
    <p
      class="footer-status"
      :class="{ 'is-saved': savedFlash && !changeCount }"
    >
      <i
        v-if="savedFlash && !changeCount && !saving"
        class="mdi mdi-check status-icon"
      />{{ status }}
    </p>
    <div class="footer-actions">
      <template v-if="readOnly">
        <PosUiButton color="neutral" variant="outline" @click="emit('close')">{{
          $t('close')
        }}</PosUiButton>
      </template>
      <template v-else-if="create">
        <PosUiButton
          v-if="createAnother"
          class="action-another"
          color="neutral"
          variant="outline"
          :disabled="saving || !online"
          :title="offlineTitle"
          @click="emit('save-another')"
          >{{ $t('menuPage.editor.createAnother') }}</PosUiButton
        >
        <PosUiButton
          :disabled="saving || !online"
          :title="offlineTitle"
          @click="emit('save')"
          >{{ createText || $t('menuPage.editor.createProduct') }}</PosUiButton
        >
      </template>
      <template v-else-if="retry && !changeCount">
        <PosUiButton
          :disabled="saving || !online"
          :title="offlineTitle"
          @click="emit('retry')"
          >{{ $t('menuPage.editor.saveRest') }}</PosUiButton
        >
      </template>
      <template v-else-if="changeCount">
        <PosUiButton
          class="action-discard"
          color="neutral"
          variant="outline"
          :disabled="saving"
          @click="emit('discard')"
          >{{ $t('menuPage.editor.discard') }}</PosUiButton
        >
        <PosUiButton
          :disabled="saving || !online || saveDisabled"
          :title="offlineTitle"
          @click="emit('save')"
          >{{ $t('save') }}</PosUiButton
        >
      </template>
      <PosUiButton
        v-else
        color="neutral"
        variant="outline"
        @click="emit('close')"
        >{{ $t('close') }}</PosUiButton
      >
    </div>
  </footer>
</template>

<style scoped lang="scss">
  .v2-catalog-editor-footer {
    @apply tw-flex tw-flex-none tw-items-center tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-4 tw-py-3;

    .footer-status {
      @apply tw-m-0 tw-mr-3 tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-neutral-500;

      &.is-saved {
        @apply tw-text-emerald-700;
      }

      .status-icon {
        @apply tw-mr-1;
      }
    }

    // Margins, not flex gap (Chrome < 84). Long labels (the create buttons
    // in German or French on a phone) wrap to a second line.
    .footer-actions {
      @apply -tw-m-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-justify-end;
      flex: 0 1 auto;

      > * {
        @apply tw-m-1;
      }
    }
  }
</style>
